import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { ApprovalType, FlatTraceLink, TraceCountTypes } from "@/types";
import {
  approvalTypeOptions,
  approvalColumns,
  traceCountOptions,
} from "@/util";
import {
  approvalStore,
  appStore,
  permissionStore,
  projectStore,
  subtreeStore,
  traceApiStore,
  traceGenerationApiStore,
} from "@/hooks";
import { Routes } from "@/router";
import {
  PanelCard,
  GroupableTable,
  MultiselectInput,
  TextButton,
  AttributeChip,
  SelectInput,
} from "@/components/common";
import { TraceLinkApproval, TraceLinkDisplay } from "@/components/traceLink";


/**
 Displays a table of generated trace links for approval.
 */
const __default__ = {
  name: "ApprovalTable",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const approvalOptions = approvalTypeOptions();
const countOptions = traceCountOptions();

const customCells: (keyof FlatTraceLink | string)[] = [
  "sourceType",
  "targetType",
  "approvalStatus",
  "score",
  "actions",
];

const currentRoute = useRoute();

const countType = ref<TraceCountTypes>("all");
const approvalTypes = ref<ApprovalType[]>([]);
const groupBy = ref<string | undefined>("targetName");

const displayActions = computed(() =>
  permissionStore.isAllowed("project.edit_data")
);

const rows = computed(() => approvalStore.traceLinks);

const loading = computed(() => appStore.isLoading > 0);

const expanded = computed<string[]>({
  get() {
    return approvalStore.expandedIds;
  },
  set(ids) {
    approvalStore.expandedIds = ids;
  },
});

/**
 * @return What parts of the expansion panel to show.
 */
const expandedDisplay = computed<undefined | "source" | "target">(() => {
  switch (groupBy.value) {
    case "sourceName":
      return "target";
    case "targetName":
      return "source";
    default:
      return undefined;
  }
});

const columns = computed(() =>
  approvalColumns.filter(
    (col) =>
      !(
        (approvalTypes.value.length === 1 && col.name === "approvalStatus") ||
        (groupBy.value === "sourceName" && col.name === "sourceType") ||
        (groupBy.value === "targetName" && col.name === "targetType")
      )
  )
);

/**
 * Refreshes table data.
 */
function handleRefresh() {
  traceGenerationApiStore.handleReload();
}

/**
 * Filters out rows that don't match the selected approval types.
 * @param row - The row to filter.
 * @return Whether to keep the row.
 */
function filterRow(row: FlatTraceLink): boolean {
  const sourceSubtree = subtreeStore.getSubtreeItem(row.sourceId);
  const targetSubtree = subtreeStore.getSubtreeItem(row.targetId);
  const bothTraced =
    sourceSubtree.neighbors.length > 0 && targetSubtree.neighbors.length > 0;

  return (
    !traceApiStore.loadingTraceIds.includes(row.traceLinkId) &&
    (countType.value === "all" ||
      (countType.value === "onlyTraced" && bothTraced) ||
      (countType.value === "notTraced" && !bothTraced)) &&
    (approvalTypes.value.length === 0 ||
      approvalTypes.value.includes(row.approvalStatus))
  );
}

onMounted(() => handleRefresh());

watch(
  () => currentRoute.path,
  (path) => {
    if (path !== Routes.ARTIFACT) return;

    handleRefresh();
  }
);

watch(
  () => projectStore.versionId,
  (versionId) => {
    if (!versionId) return;

    handleRefresh();
  }
);

/**
 * Expands all panels in the group.
 * @param groupBy - The grouped field.
 * @param groupValue - The grouped value.
 */
function handleOpenGroup(groupBy: keyof FlatTraceLink, groupValue: unknown) {
  approvalStore.expandLinks((link) => link[groupBy] === groupValue);
}

/**
 * Collapses all panels in the group.
 * @param groupBy - The grouped field.
 * @param groupValue - The grouped value.
 */
function handleCloseGroup(groupBy: keyof FlatTraceLink, groupValue: unknown) {
  approvalStore.collapseLinks((link) => link[groupBy] !== groupValue);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(PanelCard), null, {
    default: _withCtx(() => [
      _createVNode(_unref(GroupableTable), {
        "group-by": groupBy.value,
        "onUpdate:groupBy": _cache[3] || (_cache[3] = ($event: any) => ((groupBy).value = $event)),
        expanded: expanded.value,
        "onUpdate:expanded": _cache[4] || (_cache[4] = ($event: any) => ((expanded).value = $event)),
        expandable: "",
        columns: columns.value,
        rows: rows.value,
        "row-key": "traceLinkId",
        loading: loading.value,
        "default-sort-by": "targetName",
        "default-group-by": groupBy.value,
        "filter-row": filterRow,
        "custom-cells": customCells,
        "data-cy": "table-trace-approval",
        "onGroup:open": handleOpenGroup,
        "onGroup:close": handleCloseGroup
      }, {
        "header-right": _withCtx(() => [
          _createVNode(_unref(MultiselectInput), {
            modelValue: approvalTypes.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((approvalTypes).value = $event)),
            outlined: "",
            dense: "",
            "use-chips": false,
            options: _unref(approvalOptions),
            label: "Approval Types",
            "option-to-value": "",
            "option-value": "id",
            "option-label": "name",
            class: "table-input q-mb-sm q-mr-sm",
            "data-cy": "input-approval-type",
            b: ""
          }, null, 8, ["modelValue", "options"]),
          _createVNode(_unref(SelectInput), {
            modelValue: countType.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((countType).value = $event)),
            outlined: "",
            dense: "",
            options: _unref(countOptions),
            "option-to-value": "",
            "option-label": "name",
            "option-value": "id",
            label: "Visible Artifacts",
            b: "",
            class: "table-input q-mb-sm",
            "data-cy": "input-trace-table-count"
          }, null, 8, ["modelValue", "options"]),
          _createVNode(_unref(TextButton), {
            text: "",
            small: "",
            label: "Clear Unreviewed",
            icon: "trace-decline-all",
            color: "negative",
            class: "q-ml-sm",
            onClick: _unref(traceApiStore).handleDeclineAll
          }, null, 8, ["onClick"])
        ]),
        "body-cell-sourceType": _withCtx(({ row }: { row: FlatTraceLink }) => [
          _createVNode(_unref(AttributeChip), {
            value: row.sourceType,
            "artifact-type": ""
          }, null, 8, ["value"])
        ]),
        "body-cell-targetType": _withCtx(({ row }: { row: FlatTraceLink }) => [
          _createVNode(_unref(AttributeChip), {
            value: row.targetType,
            "artifact-type": ""
          }, null, 8, ["value"])
        ]),
        "body-cell-approvalStatus": _withCtx(({ row }: { row: FlatTraceLink }) => [
          _createVNode(_unref(AttributeChip), {
            value: row.approvalStatus,
            "approval-type": ""
          }, null, 8, ["value"])
        ]),
        "body-cell-score": _withCtx(({ row }: { row: FlatTraceLink }) => [
          _createVNode(_unref(AttributeChip), {
            value: row.score,
            "confidence-score": ""
          }, null, 8, ["value"])
        ]),
        "body-cell-actions": _withCtx(({ row }) => [
          _createElementVNode("div", {
            onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
          }, [
            (displayActions.value)
              ? (_openBlock(), _createBlock(_unref(TraceLinkApproval), {
                  key: 0,
                  trace: row
                }, null, 8, ["trace"]))
              : _createCommentVNode("", true)
          ])
        ]),
        "body-expanded": _withCtx(({ row }) => [
          _createVNode(_unref(TraceLinkDisplay), {
            trace: row,
            "show-only": expandedDisplay.value
          }, null, 8, ["trace", "show-only"])
        ]),
        _: 1
      }, 8, ["group-by", "expanded", "columns", "rows", "loading", "default-group-by"])
    ]),
    _: 1
  }))
}
}

})